import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import {
  centsToInput,
  eurosToCents,
  formatCents,
  microsToEuros,
  useAddAdminBillingReduction,
  useAdminBillingPrices,
  useDeleteAdminBillingInvoiceLine,
  type AdminBillingInvoiceDetail,
} from '../../hooks/use-admin-billing'
import { apiErrorMessage } from './billing-errors'

/** BillingInvoiceLines shows the lines of an invoice and, on a draft, its reductions. */
export function BillingInvoiceLines({ invoice }: { invoice: AdminBillingInvoiceDetail }) {
  const { t, i18n } = useTranslation('admin')
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'
  const { data: prices } = useAdminBillingPrices()
  const add = useAddAdminBillingReduction()
  const remove = useDeleteAdminBillingInvoiceLine()
  const [amount, setAmount] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const nf = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 })
  const draft = invoice.status === 'draft'
  const prepaid = invoice.kind !== 'monthly'

  const codeLabel = (code: string) => {
    const found = prices?.codes.find((c) => c.code === code)
    return found ? (found.label[lang] ?? found.label.en) : code
  }
  const unit = (code: string) => prices?.codes.find((c) => c.code === code)?.billing_unit ?? ''

  const submit = () => {
    const cents = eurosToCents(amount)
    if (!cents || !label.trim()) {
      setError(t('billing.invoices.reduction.invalid'))
      return
    }
    setError(null)
    add.mutate(
      { params: { path: { uid: invoice.uid } }, body: { amount_cents: cents, label: label.trim() } },
      {
        onSuccess: () => {
          setAmount('')
          setLabel('')
        },
        onError: (e) => setError(apiErrorMessage(e, t('billing.invoices.reduction.error'))),
      },
    )
  }

  return (
    <section className="space-y-4" data-testid="billing-invoice-lines">
      <div>
        <h2 className="text-lg font-semibold">{t('billing.invoices.lines_title')}</h2>
        {prepaid && <p className="text-sm text-muted-foreground" data-testid="billing-invoice-annual-rate">{t('billing.invoices.annual_rate')}</p>}
      </div>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('billing.invoices.line.label')}</TableHead>
              <TableHead className="text-right">{t('billing.consumption.column.quantity')}</TableHead>
              <TableHead className="text-right">{t('billing.invoices.line.months')}</TableHead>
              <TableHead className="text-right">{t('billing.consumption.column.unit_price')}</TableHead>
              <TableHead className="text-right">{t('billing.consumption.column.amount')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoice.lines.map((l) => {
              const reduction = l.code === 'reduction'
              return (
                <TableRow key={l.uid} data-testid={`billing-invoice-line-${l.code}`}>
                  <TableCell>{reduction ? l.label : codeLabel(l.code)}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {reduction ? '' : `${nf.format(l.quantity / l.divisor)} ${l.divisor > 1 ? unit(l.code) : ''}`.trim()}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{reduction ? '' : l.months}</TableCell>
                  <TableCell className="text-right tabular-nums">{reduction ? '' : microsToEuros(l.unit_price_micros, i18n.language)}
                    {!reduction && l.price_period === 'year' && <span className="text-xs text-muted-foreground"> {t('billing.prices.period.year')}</span>}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCents(l.amount_cents, i18n.language)}</TableCell>
                  <TableCell className="text-right">
                    {draft && reduction && (
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate({ params: { path: { uid: l.uid } } })}
                        data-testid={`billing-reduction-delete-${l.uid}`}
                      >
                        {t('billing.prices.delete')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {draft && (
        <div className="bg-card rounded-lg border border-border p-4 flex flex-wrap items-end gap-3" data-testid="billing-reduction-form">
          <div className="space-y-1">
            <Label>{t('billing.invoices.reduction.amount')}</Label>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder={centsToInput(1000)} className="w-36" data-testid="billing-reduction-amount" />
          </div>
          <div className="space-y-1 grow">
            <Label>{t('billing.invoices.reduction.label')}</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t('billing.invoices.reduction.placeholder')} data-testid="billing-reduction-label" />
          </div>
          <Button onClick={submit} disabled={add.isPending} data-testid="billing-reduction-add">
            {t('billing.invoices.reduction.add')}
          </Button>
          {error && (
            <div className="w-full text-sm text-destructive" data-testid="billing-reduction-error">
              {error}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
